import { Button, Spinner, Tooltip, TooltipContent, TooltipTrigger } from '@/ui-kit'
import { formatNumber } from '@/utils'
import { type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { type EnrollmentSubmission, type SubmissionDisplayState, useFetchEnrollmentSubmissions } from '../api'

const PAGE_LIMIT = 50

type SubmissionsSubTableProps = {
  enrollmentId: string
  enabled: boolean
}

const STATE_CLASS: Record<SubmissionDisplayState, string> = {
  UNDER_REVIEW: 'text-orange',
  APPROVED: 'text-[var(--colorGreen)]',
  REJECTED: 'text-[var(--colorError)]'
}
const STATE_LABEL: Record<SubmissionDisplayState, MessageDescriptor> = {
  UNDER_REVIEW: msg`Under review`,
  APPROVED: msg`Approved`,
  REJECTED: msg`Rejected`
}

const StateCell = ({ submission, className }: { submission: EnrollmentSubmission; className?: string }) => {
  const { t } = useLingui()
  const state = submission.displayState
  const label = t(STATE_LABEL[state])

  const content = <span className={clsx('font-medium', STATE_CLASS[state], className)}>{label}</span>

  if (submission.submissionStatus === 'FAILED') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>{content}</span>
        </TooltipTrigger>
        <TooltipContent>
          <Trans>Processing failed</Trans>
        </TooltipContent>
      </Tooltip>
    )
  }

  return content
}

export const SubmissionsSubTable = ({ enrollmentId, enabled }: SubmissionsSubTableProps) => {
  const query = useFetchEnrollmentSubmissions({
    id: enrollmentId,
    limit: PAGE_LIMIT,
    enabled
  })

  const entries = query.data?.pages.flatMap((p) => p.data) ?? []
  const hasMore = !!query.hasNextPage
  const isFirstLoad = query.isPending && enabled
  const isFetchingMore = query.isFetchingNextPage

  return (
    <div className="flex flex-col gap-3">
      {isFirstLoad ? (
        <div className="flex w-full justify-center py-6">
          <Spinner size="small" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-body-2 text-gray-90 rounded-xl bg-gray-100/30 p-4 text-center">
          <Trans>No submissions in this enrollment yet</Trans>
        </div>
      ) : (
        <>
          <div className="max-tablet:hidden">
            <table className="w-full table-auto border-collapse">
              <tbody>
                {entries.map((sub) => (
                  <tr key={sub.id} className={'border-b border-solid border-gray-100/60'}>
                    <td className="text-body-2 px-4 py-3">
                      {sub.id.substring(0, 4)}...{sub.id.substring(sub.id.length - 4)}
                    </td>
                    <td className="text-body-2 px-4 py-3">{dayjs(sub.createdAt).format('ll · HH:mm')}</td>
                    <td className="text-body-2 w-[250px] px-4 py-3">
                      <StateCell submission={sub} />
                    </td>
                    <td className="text-body-2 w-30 px-4 py-3 text-right">
                      {sub.amountUsd ? `$${formatNumber(sub.amountUsd ?? 0, 2)}` : ''}
                    </td>
                    <td className="w-10"></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile — stacked rows */}
          <div className="tablet:hidden flex flex-col gap-2">
            {entries.map((sub) => (
              <div key={sub.id} className="rounded-xl border border-solid border-gray-100 bg-gray-100/20 p-3">
                <div className="text-body-2 flex items-center justify-between gap-3">
                  <span>
                    {sub.id.substring(0, 4)}...{sub.id.substring(sub.id.length - 4)}
                  </span>
                  <span className="text-gray-90">{dayjs(sub.createdAt).format('ll · HH:mm')}</span>
                </div>
                <div className="mt-1 flex items-center justify-between gap-3">
                  <StateCell className="text-body-2" submission={sub} />
                  <div className="text-right">{sub.amountUsd ? `$${formatNumber(sub.amountUsd ?? 0, 2)}` : ''}</div>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-3">
              <Button
                variant="bordered-gray"
                size="small"
                onClick={() => query.fetchNextPage()}
                isLoading={isFetchingMore}
              >
                <Trans>Load more</Trans>
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
